import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchSessionSnapshot } from '@/api/competitionApi';
import { exitCompetitionToHome } from '@/hooks/useCompetitionSession';
import type { VerificationResponse } from '@/types';

// Focus/visibility is the primary sync trigger; polling is a low-frequency backup.
const POLL_INTERVAL_MS = 60_000;

interface Options {
  enabled: boolean;
  /** Called on /turniej when another tab saved progress (server wins). */
  onActiveSync?: (data: VerificationResponse) => void;
}

/**
 * Polls GET /sesja: detects test finished elsewhere + syncs index/answers on /turniej.
 */
export function useCompetitionCompletedGuard({ enabled, onActiveSync }: Options): void {
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const onActiveSyncRef = useRef(onActiveSync);
  onActiveSyncRef.current = onActiveSync;

  useEffect(() => {
    if (!enabled) return;

    handledRef.current = false;

    const handleTerminalState = async (state: 'completed' | 'deadline') => {
      if (handledRef.current) return;
      handledRef.current = true;
      await exitCompetitionToHome(state);
      navigate('/', { replace: true });
    };

    const probe = async (opts?: { requireVisible?: boolean }) => {
      if (handledRef.current) return;
      if (opts?.requireVisible !== false && document.visibilityState !== 'visible') return;

      try {
        const snapshot = await fetchSessionSnapshot();
        if (snapshot.state === 'completed' || snapshot.state === 'deadline') {
          await handleTerminalState(snapshot.state);
          return;
        }
        if (snapshot.state === 'active' && onActiveSyncRef.current) {
          onActiveSyncRef.current(snapshot.data);
        }
      } catch {
        // Network blip — next poll or focus will retry.
      }
    };

    // Run once on mount (only if visible).
    void probe({ requireVisible: true });

    // Low-frequency backup polling (only when visible).
    const intervalId = window.setInterval(() => {
      void probe({ requireVisible: true });
    }, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // Primary path: when user returns to the tab/window, sync immediately.
        void probe({ requireVisible: false });
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, navigate]);
}
